/**
 * Author & Publisher Addendum Page
 * 
 * Legal document for NewAudia Systems, LLC
 * Version 1.1 | Effective Date: March 1, 2026
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, PenTool, Mail } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { useAuth } from '../contexts/AuthContext';
import { Seo, PAGE_SEO } from '../components/Seo';

const AuthorPublisherAddendumPage = () => {
  const { user } = useAuth();
  
  const getHomeRoute = () => {
    if (!user) return '/';
    const roles = user.roles || [];
    if (roles.includes('admin')) return '/admin';
    if (roles.includes('publisher')) return '/publisher';
    if (roles.includes('author') || roles.includes('creator')) return '/creator';
    if (roles.includes('reader')) return '/home';
    return '/';
  };
  
  const homeRoute = getHomeRoute();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <Seo {...PAGE_SEO.addendum} />
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={homeRoute} className="flex items-center gap-3">
            <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-10 w-auto rounded-lg" />
          </Link>
          <Link 
            to={homeRoute}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Document Header */}
        <div className="mb-12 pb-8 border-b border-slate-700">
          <div className="flex items-center gap-4 mb-6">
            <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-16 w-auto rounded-lg" />
            <div>
              <h1 data-testid="addendum-company-name" className="text-3xl font-bold text-white">NewAudia Systems, LLC</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <PenTool className="w-6 h-6 text-indigo-400" />
            <h2 data-testid="addendum-title" className="text-2xl font-semibold text-white">Author & Publisher Addendum</h2>
          </div>
          
          <p className="text-sm text-slate-400 mb-3">Supplement to the Author Publishing Agreement</p>
          <div className="flex flex-wrap gap-6 text-sm text-slate-400">
            <p><span className="text-slate-500">Effective Date:</span> March 1, 2026</p>
            <p><span className="text-slate-500">Version:</span> 1.1</p>
            <p><span className="text-slate-500">Last Updated:</span> March 2026</p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-slate max-w-none">
          {/* Important Notice */}
          <section className="mb-10">
            <p className="text-amber-400 font-semibold text-sm uppercase tracking-wide mb-4">Important — Please Read Carefully</p>
            <p className="text-slate-300 leading-relaxed">
              This Author & Publisher Addendum ("Addendum") supplements and is incorporated into the NewAudia Author 
              Publishing Agreement ("Agreement"). It applies to all Authors and Publishers who access or use the NewAudia 
              platform. In the event of any conflict between this Addendum and the Agreement, the Agreement controls. 
              Capitalized terms not defined herein have the meanings given to them in the Agreement.
            </p>
          </section>

          {/* Company Identification */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Company Identification</h3>
            <p className="text-slate-300 leading-relaxed">
              The platform is operated by <strong className="text-white">NewAudia Systems, LLC</strong>, a limited liability 
              company organized under the laws of the State of Colorado, with its principal place of business in Colorado Springs, 
              Colorado (hereinafter "NewAudia," "Company," "we," "us," or "our"). "NewAudia" is a registered trade name of 
              NewAudia Systems, LLC in the State of Colorado.
            </p>
            <p className="text-slate-300 leading-relaxed mt-4">
              This Addendum is incorporated by reference into the NewAudia Author Publishing Agreement and is further subject 
              to the NewAudia Acceptable Use Policy and AI Usage Policy. Together, these documents form the complete legal 
              framework governing Author and Publisher use of the NewAudia platform.
            </p>
          </section>

          {/* Parties */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Parties</h3>
            <p className="text-slate-300 leading-relaxed">
              This Addendum is entered into by and between NewAudia Systems, LLC ("NewAudia") and the Author or Publisher 
              identified during account registration on the NewAudia platform ("Author" or "Publisher," as applicable). 
              Authors are individuals who upload and publish their own Manuscripts. Publishers are entities that manage one 
              or more Managed Authors through the Publisher Portal.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">1. Eligibility and Verification</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">1.1 Eligibility Requirements</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              To publish Manuscripts on the NewAudia platform, Authors and Publishers must:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Be at least 18 years of age or the age of majority in their jurisdiction, whichever is greater</li>
              <li>Have full legal authority to enter into the Author Publishing Agreement and this Addendum</li>
              <li>Provide accurate account registration information, including a valid email address and payment details via Stripe</li>
              <li>Provide tax identification information as requested by NewAudia prior to Commission payment issuance</li>
            </ul>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">1.2 Verification Rights</h4>
            <p className="text-slate-300 leading-relaxed">
              NewAudia reserves the right to require Authors and Publishers to verify their identity, legal authority over 
              submitted Manuscripts, tax compliance status, and any other information NewAudia deems necessary to operate 
              the platform in compliance with applicable law. NewAudia may suspend publication privileges pending completion 
              of any verification request.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">1.3 Content Acceptance</h4>
            <p className="text-slate-300 leading-relaxed">
              Submission of a Manuscript does not guarantee publication. NewAudia may reject or remove any Manuscript at 
              its sole discretion consistent with Article 3 of the Author Publishing Agreement and Section 5 of the 
              Acceptable Use Policy. NewAudia assumes no obligation to provide a reason for any rejection or removal decision.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">2. Ownership and Licensing</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">2.1 Author Retains All Rights</h4>
            <p className="text-slate-300 leading-relaxed">
              Author retains full and exclusive ownership of all copyrights, trademarks, moral rights, and other intellectual 
              property rights in and to each Manuscript submitted to the Platform. This Addendum and the Author Publishing 
              Agreement do not constitute a transfer or assignment of any ownership rights in any Manuscript. Authors are not 
              restricted from simultaneously publishing, distributing, or licensing their Manuscripts through any other platform, 
              retailer, or channel. For full terms, see Article 4 of the Author Publishing Agreement.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">2.2 License Grant — Scope</h4>
            <p className="text-slate-300 leading-relaxed">
              By submitting a Manuscript, Author grants NewAudia a non-exclusive, royalty-free, worldwide license to process, 
              store, narrate, distribute, and display the Manuscript as necessary to deliver the platform's services, including 
              Audiobook production via the seven-step ingestion pipeline, Ephemeral Intelligence generation for the Audia AI 
              reading assistant, and promotional use of Manuscript titles, cover images, and sample content. This license is 
              coextensive with and governed by Article 4.2 of the Author Publishing Agreement.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">2.3 AI Processing Consent</h4>
            <p className="text-slate-300 leading-relaxed">
              Author acknowledges and consents to NewAudia processing Manuscript content through artificial intelligence systems, 
              including OpenAI's Text-to-Speech API for Audiobook narration and NewAudia's RAG-based indexing system to generate 
              Ephemeral Intelligence data for the Audia AI reading assistant. This processing is described in full in the NewAudia 
              AI Usage Policy and Article 4.4 of the Author Publishing Agreement. NewAudia does not authorize third-party AI 
              providers to use Manuscript content to train general-purpose AI models.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">3. Representations and Warranties</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              In addition to the representations and warranties set forth in Article 6.1 of the Author Publishing Agreement, 
              Author and Publisher each represent, warrant, and covenant to NewAudia that:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>They possess full legal authority to execute this Addendum and perform all obligations hereunder</li>
              <li>Each Manuscript submitted is original and does not infringe any third-party copyright, trademark, trade secret, right of publicity, or other intellectual property right</li>
              <li>Each Manuscript complies with all applicable local, state, federal, and international laws and regulations</li>
              <li>No Manuscript submitted is currently the subject of any claim, demand, or proceeding alleging copyright infringement or other violation of law, without prior written disclosure to NewAudia</li>
              <li>All account registration information, tax identification data, and Stripe payment details are accurate, complete, and will be kept current</li>
              <li>Publishers represent that they have obtained all necessary authorizations from each Managed Author to submit that Author's Manuscript on their behalf and to receive Commission payments in connection with that Manuscript</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">4. Revenue, Commissions, and Reporting</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.1 Commission Structure</h4>
            <p className="text-slate-300 leading-relaxed">
              Authors and Publishers earn a Commission equal to fifty percent (50%) of the monetary value of all Reader 
              Credits consumed in connection with their published Audiobooks during each monthly Reporting Period. Commission 
              terms, payment schedules, minimum thresholds, and dispute procedures are governed exclusively by Article 5 of 
              the Author Publishing Agreement. In the event of any conflict between this Section and Article 5 of the 
              Agreement, the Agreement controls.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.2 No Income Guarantee</h4>
            <div className="bg-amber-950/30 border border-amber-900/50 rounded-lg p-5 mt-2">
              <p className="text-slate-300 leading-relaxed text-sm uppercase tracking-wide">
                No Income Guarantee. NewAudia makes no representation, warranty, or guarantee of any kind that Author or 
                Publisher will earn any Commission, generate any revenue, or achieve any specific level of reader engagement. 
                Actual Commission amounts depend entirely on voluntary reader activity and are not within NewAudia's control 
                or prediction. Any investment of time or upload fees is made at Author's or Publisher's sole risk.
              </p>
            </div>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.3 Reader Engagement Reports</h4>
            <p className="text-slate-300 leading-relaxed">
              NewAudia will make available monthly Reader Engagement Reports via the Author's or Publisher's platform 
              dashboard on or before the fifteenth (15th) day following the close of each Reporting Period. Reports detail 
              Reader Credit consumption per title, Commission calculations, and cumulative earnings. Full reporting terms 
              are set forth in Section 5.5 of the Author Publishing Agreement.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.4 Ingestion Fees</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              The first successful Manuscript ingestion per Author account is provided at no charge. Each subsequent 
              successful ingestion is assessed a fee of <strong className="text-white">$9.99 USD</strong>, charged via 
              Stripe upon pipeline completion.
            </p>
            <p className="text-slate-300 leading-relaxed text-sm">
              All ingestion fees are non-refundable once a Manuscript has been submitted for processing, regardless of outcome. 
              Fees paid in connection with any Manuscript found to violate the Author Publishing Agreement, this Addendum, or 
              the Acceptable Use Policy — or associated with a suspended or permanently banned account — are expressly and 
              irrevocably non-refundable. See Section 5.1 and 5.2 of the Author Publishing Agreement.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">5. Publisher Portal — Additional Terms for Publishers</h3>
            <p className="text-slate-300 leading-relaxed mb-6 italic text-sm">
              This section applies exclusively to users operating as Publishers through the NewAudia Publisher Portal. 
              Individual Authors publishing their own work are not subject to this section.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">5.1 Publisher Role and Responsibilities</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              Publishers use the Publisher Portal to manage one or more Managed Authors — individuals whose Manuscripts 
              the Publisher uploads, administers, and distributes through the NewAudia platform on the Author's behalf. 
              Publishers are solely responsible for:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Obtaining valid written authorization from each Managed Author before submitting any Manuscript on that Author's behalf</li>
              <li>Ensuring each Managed Author has agreed to the NewAudia Author Publishing Agreement, Acceptable Use Policy, and AI Usage Policy prior to Manuscript submission</li>
              <li>Ensuring all Manuscripts submitted through the Publisher Portal comply with the Author Publishing Agreement's Community Standards (Article 3) and the Acceptable Use Policy</li>
              <li>Maintaining accurate contact and payment information for Commission disbursement</li>
              <li>Notifying NewAudia promptly if a Managed Author relationship is terminated, and requesting removal of that Author's Manuscripts if required</li>
            </ul>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">5.2 Publisher Liability for Managed Author Content</h4>
            <p className="text-slate-300 leading-relaxed">
              Publishers bear joint responsibility with their Managed Authors for the content of all Manuscripts submitted 
              through the Publisher Portal. NewAudia may hold the Publisher liable for any Community Standards violation, 
              copyright infringement claim, or misrepresentation of authorship related to a Managed Author's Manuscript, 
              consistent with Article 6.2 of the Author Publishing Agreement. The Publisher's indemnification obligations 
              under that Article extend to all Manuscripts submitted through their Publisher account.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">5.3 Commission Disbursement for Managed Authors</h4>
            <p className="text-slate-300 leading-relaxed">
              Unless NewAudia has a separate written arrangement with a Managed Author, Commission payments are disbursed 
              to the Publisher's Stripe account on file. The Publisher is solely responsible for any further distribution 
              of earnings to Managed Authors in accordance with whatever arrangement exists between the Publisher and those 
              Authors. NewAudia has no obligation, responsibility, or liability with respect to any payment disputes between 
              Publishers and their Managed Authors.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">5.4 Prohibited Publisher Conduct</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              In addition to the prohibited conduct set forth in the Acceptable Use Policy (Section 3.5), Publishers may not:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Submit Manuscripts on behalf of Authors who have been previously banned or suspended from the NewAudia platform</li>
              <li>Represent themselves as the Author of any Manuscript for which they are acting solely as Publisher/distributor</li>
              <li>Collect or retain Commission payments for a Managed Author's Manuscript without that Author's knowledge and consent</li>
              <li>Use the Publisher Portal to circumvent ingestion fees, content restrictions, or any other platform policy applicable to individual Authors</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">6. Takedowns and Content Disputes</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">6.1 NewAudia's Removal Rights</h4>
            <p className="text-slate-300 leading-relaxed">
              NewAudia may remove, restrict, or withhold from public access any Manuscript or Audiobook at any time, with 
              or without notice, at its sole and unreviewable discretion. Grounds for removal include, but are not limited to: 
              disputed ownership or licensing rights, copyright infringement claims, violation of Community Standards, violation 
              of the Acceptable Use Policy, platform policy changes, or any other reason NewAudia deems appropriate. NewAudia's 
              exercise of this right shall not be deemed a breach of this Addendum or the Author Publishing Agreement. See 
              Article 3.4 of the Author Publishing Agreement.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">6.2 Third-Party Infringement Claims</h4>
            <p className="text-slate-300 leading-relaxed">
              If NewAudia receives a claim that a Manuscript infringes the intellectual property rights of a third party, 
              NewAudia reserves the right to immediately remove the relevant Audiobook from public access and cooperate fully 
              with any resulting legal proceedings. Authors and Publishers are solely responsible for all costs and liabilities 
              arising from infringement claims relating to their Manuscripts, consistent with Article 6.2 of the Author 
              Publishing Agreement.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">6.3 Dispute Reporting</h4>
            <p className="text-slate-300 leading-relaxed">
              Authors or Publishers who believe a removal decision was made in error, or who wish to report a third-party 
              content violation, should contact NewAudia at{' '}
              <a href="mailto:admin@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                admin@newaudia.com
              </a>{' '}
              with sufficient detail to identify the content in question. NewAudia is not obligated to reverse any removal 
              decision and will respond to dispute reports at its reasonable discretion.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">7. Suspension, Termination, and Permanent Bans</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">7.1 Termination</h4>
            <p className="text-slate-300 leading-relaxed">
              This Addendum is coterminous with the Author Publishing Agreement. Termination of the Agreement by either Party, 
              in accordance with Article 7 of that Agreement, terminates this Addendum simultaneously. The surviving provisions 
              of Article 7.6 of the Agreement also survive termination of this Addendum.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">7.2 Permanent Account Ban</h4>
            <div className="bg-red-950/30 border border-red-900/50 rounded-lg p-6 mt-2">
              <h5 className="text-lg font-semibold text-red-400 mb-3">Permanent Ban Policy</h5>
              <p className="text-slate-300 leading-relaxed text-sm mb-3">
                In cases of egregious, repeated, or malicious violations of this Addendum, the Author Publishing Agreement, 
                or the Acceptable Use Policy, NewAudia may permanently ban an Author's or Publisher's account and all associated 
                or related accounts. A permanent ban results in:
              </p>
              <ul className="list-none text-slate-300 space-y-1 text-sm">
                <li>(a) immediate removal of all associated Manuscripts and Audiobooks from public access;</li>
                <li>(b) forfeiture of any unpaid Commissions accrued in connection with non-compliant Manuscripts;</li>
                <li>(c) non-refundability of all ingestion fees paid; and</li>
                <li>(d) prohibition on creating new accounts on the platform, including under different names, email addresses, or entities.</li>
              </ul>
              <p className="text-slate-300 leading-relaxed text-sm mt-3">
                NewAudia's determination is made at its sole discretion and is not subject to internal appeal.
              </p>
            </div>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">7.3 Effect on Publisher Portal Access</h4>
            <p className="text-slate-300 leading-relaxed">
              A permanent ban on a Publisher account results in immediate revocation of Publisher Portal access and removal 
              of all Manuscripts submitted through that Publisher's account. NewAudia will use commercially reasonable efforts 
              to notify affected Managed Authors of the removal of their Manuscripts, but bears no liability to Managed Authors 
              arising from the Publisher's violation of this Addendum or the Author Publishing Agreement.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">8. General Provisions</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">8.1 Relationship to Author Publishing Agreement</h4>
            <p className="text-slate-300 leading-relaxed">
              This Addendum supplements and is incorporated into the Author Publishing Agreement. In the event of any conflict 
              between this Addendum and the Author Publishing Agreement, the Author Publishing Agreement controls. All defined 
              terms used in this Addendum that are not separately defined herein have the meanings given to them in the Author 
              Publishing Agreement.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">8.2 Governing Law</h4>
            <p className="text-slate-300 leading-relaxed">
              This Addendum is governed by the laws of the State of Colorado, without regard to conflict of law principles, 
              consistent with Section 8.1 of the Author Publishing Agreement.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">8.3 Amendments</h4>
            <p className="text-slate-300 leading-relaxed">
              NewAudia may amend this Addendum at any time by posting a revised version on the Platform and providing notice 
              to Author's or Publisher's email address on file. Continued use of the Platform following the effective date of 
              any amendment constitutes acceptance of the revised Addendum.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">8.4 Electronic Acceptance</h4>
            <p className="text-slate-300 leading-relaxed">
              Author's or Publisher's acceptance of this Addendum may be evidenced by electronic signature, by checking an 
              acceptance checkbox during account registration or Manuscript upload, or by submission of a Manuscript to the 
              Platform following notice of this Addendum. Any such acceptance shall constitute a legally binding signature.
            </p>
          </section>

          {/* Signatures */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Signatures</h3>
            <p className="text-slate-300 leading-relaxed mb-6 italic">
              In witness whereof, the Parties have executed this Author & Publisher Addendum as of the Effective Date.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-5">
                <p className="text-white font-semibold mb-3">NewAudia Systems, LLC</p>
                <div className="space-y-2 text-sm text-slate-400">
                  <p>Authorized Signature: ___________________</p>
                  <p>Printed Name & Title: ___________________</p>
                  <p>Date: ___________________</p>
                </div>
              </div>
              <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-5">
                <p className="text-white font-semibold mb-3">Author / Publisher</p>
                <div className="space-y-2 text-sm text-slate-400">
                  <p>Signature: ___________________</p>
                  <p>Printed Name: ___________________</p>
                  <p>Date: ___________________</p>
                  <p>Email Address on File: ___________________</p>
                  <p>Role: Author / Publisher</p>
                  <p>Pen Name / Publishing Entity: ___________________</p>
                </div>
              </div>
            </div>
          </section>

          {/* Version History */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Version History</h3>
            <div className="overflow-x-auto">
              <table data-testid="addendum-version-history" className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-20">Version</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-36">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Summary of Changes</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-slate-300">1.0</td>
                    <td className="py-3 px-4 text-slate-400">January 1, 2026</td>
                    <td className="py-3 px-4 text-slate-400">Initial publication. Entity listed as NewAudia dba Use What You Got Publishing, LLC.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">1.1</td>
                    <td className="py-3 px-4 text-slate-300">March 2026</td>
                    <td className="py-3 px-4 text-slate-400">
                      Entity updated to NewAudia Systems, LLC (registered trade name in Colorado). Added eligibility and 
                      verification requirements, AI processing consent, expanded representations and warranties, ingestion 
                      fee details with non-refundability language, Publisher Portal terms (Sections 5.1–5.4), takedown and 
                      content dispute procedures, permanent ban policy, governing law, electronic acceptance. Aligned with 
                      Author Publishing Agreement v1.0, AUP v1.1, and AI Usage Policy v1.1.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Footer Entity Statement */}
          <section className="mt-12 pt-8 border-t border-slate-700">
            <p className="text-sm text-slate-500">
              NewAudia Systems, LLC | Colorado Springs, Colorado |{' '}
              <a href="https://www.newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">www.newaudia.com</a>
              {' '}| <a href="mailto:admin@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">admin@newaudia.com</a>
            </p>
            <p className="text-sm text-slate-500 mt-2 italic">
              "NewAudia" is a registered trade name of NewAudia Systems, LLC in the State of Colorado.
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Version 1.1 — March 2026
            </p>
          </section>
        </div>
      </main>

      <UniversalFooter />
    </div>
  );
};

export default AuthorPublisherAddendumPage;
